import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';

import { ApiClient, entitlementsSchema } from '@zercode/api-client';

// ==============================|| C-8 — license/feature-flag entitlements ||============================== //
// The current tenant's flattened licensed module codes drive FE gating (menu filtering, RouteGuard,
// upsell). Source of truth = ControlPlane GET /api/v1/entitlements. Backend still enforces license
// (403) — this is UX only. `modules === null` means NOT YET LOADED (gating fails OPEN to avoid a
// flash of hidden UI; the backend is the real gate).

interface EntitlementsState {
  /** flattened module codes; ['*'] = all; null = not loaded yet */
  modules: string[] | null;
  setEntitlements: (modules: string[]) => void;
  clearEntitlements: () => void;
}

export const useEntitlementsStore = create<EntitlementsState>()((set) => ({
  modules: null,
  setEntitlements: (modules) => set({ modules }),
  clearEntitlements: () => set({ modules: null })
}));

/** Pure membership check: '*' (all-modules sentinel) or an exact module code. */
export function hasModule(modules: string[] | null | undefined, moduleKey: string): boolean {
  if (!modules) return true; // not loaded → fail open (backend still enforces)
  return modules.includes('*') || modules.includes(moduleKey);
}

/** True once entitlements have been loaded (so guards can wait before deciding). */
export function useEntitlementsLoaded(): boolean {
  return useEntitlementsStore((s) => s.modules !== null);
}

/**
 * Feature flag for a module key. Returns true while entitlements are unloaded (fail open) and
 * once loaded reflects the tenant's licensed modules ('*' = all).
 */
export function useFeatureFlag(moduleKey: string): boolean {
  const modules = useEntitlementsStore((s) => s.modules);
  return hasModule(modules, moduleKey);
}

/**
 * Fetch the current tenant's entitlements from ControlPlane and seed the store. Call once after
 * authentication (pass the app's ControlPlane client). Safe to mount app-wide; React Query dedupes.
 */
export function useLoadEntitlements(client: ApiClient, enabled = true) {
  const setEntitlements = useEntitlementsStore((s) => s.setEntitlements);
  return useQuery({
    queryKey: ['zc-entitlements'],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await client.get<unknown>('/api/v1/entitlements');
      const parsed = entitlementsSchema.parse(data);
      setEntitlements(parsed.modules);
      return parsed;
    }
  });
}
